import { useEffect, useMemo, useState } from "react";
import { App, Flex, Spin, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import UserActions from "../../User/reducer";
import withPermission from "../../../hoc/withPermission";
import ButtonComponent from "../../../Components/Button";
import Icon from "../../../Icon";
import { extendedAccessRoles } from "../../../Utils/permission";
import TitleSection from "../components/TitleSection";
import EnrollmentTabs from "../../ConfigurePage/Enrollments/components/EnrollmentTabs/EnrollmentTabs";
import EnrollmentCard from "../../ConfigurePage/Enrollments/components/EnrollmentCard/EnrollmentCard";
import AdminDescriptorsList from "./components/AdminDescriptorsList";
import DescriptorListFilters from "../../ConfigurePage/Enrollments/components/DescriptorListFilters/DescriptorListFilters";
import useFetchAdminDescriptorsList from "./hooks/useFetchAdminDescriptorsList";
import EnrollmentCodesListItem from "../../ConfigurePage/Enrollments/components/EnrollmentCodesListItem/EnrollmentCodesListItem";
import useFetchVisaCodesList from "../../ConfigurePage/Enrollments/hooks/useFetchVisaCodesList";
import {
  descriptorStatuses,
  enrollmentBtnTextMap,
  getEnrollmentBtnIcon,
} from "../../ConfigurePage/utils/Enrollment.util";

import "../../ConfigurePage/Enrollments/styles.less";

const { Text } = Typography;

const PAGE_SIZE = 20;
const VISA_CODES_PAGE_SIZE = 50;

const enrollmentOptions = [
  { value: "Ethoca", label: "Ethoca", icon: "mastercard" },
  { value: "RDR", label: "RDR", icon: "visa" },
  { value: "CDRN", label: "CDRN", icon: "visa" },
];

const PermissionButtonExtended = withPermission(
  ButtonComponent,
  extendedAccessRoles
);

const AdminEnrollments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const { userInfo, isAdmin } = useSelector((state) => state.auth);
  const {
    adminEnrollments,
    adminEnrollmentsFetching,
    adminDescriptorsByEnrollment,
    admin_selected_business_account_id,
    businessAccount,
  } = useSelector((state) => state.user);
  const businessAccountId = isAdmin
    ? admin_selected_business_account_id
    : userInfo?.current_business_account_id;
  const businessAccountRole = isAdmin ? null : userInfo?.ba_role?.id;

  const [activeTab, setActiveTab] = useState("Ethoca");
  const [skipFiltersUpdate, setSkipFiltersUpdate] = useState(false);
  const [isFiltersUpdated, setIsFiltersUpdated] = useState(false);
  const [viewMode, setViewMode] = useState("descriptor");
  const [filters, setFilters] = useState({
    statuses: descriptorStatuses,
    search: "",
  });

  const enrollment = useMemo(() => {
    return adminEnrollments?.find(
      (enrollment) =>
        enrollment?.name?.toLowerCase() === activeTab?.toLowerCase()
    );
  }, [adminEnrollments, activeTab]);

  useEffect(() => {
    console.log("adminDescriptorsByEnrollment", adminDescriptorsByEnrollment);
  }, [adminDescriptorsByEnrollment]);

  const { descriptors, fetchMore, hasMore, loading } =
    useFetchAdminDescriptorsList({
      enrollmentId: enrollment?.id,
      filters,
      skipFiltersUpdate,
      setSkipFiltersUpdate,
      PAGE_SIZE,
      userEnrollmentsFetching: adminEnrollmentsFetching,
    });
  const { codes, loading: codesLoading } = useFetchVisaCodesList({
    businessAccountId,
    search: filters.search,
    PAGE_SIZE: VISA_CODES_PAGE_SIZE,
    enabled: viewMode === "codes",
    isAdmin: true,
  });
  const isEmptyDescriptorsList = useMemo(() => {
    return descriptors?.length === 0;
  }, [descriptors]);

  const btnIcon = getEnrollmentBtnIcon(enrollment?.connection_status);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setIsFiltersUpdated(true);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setIsFiltersUpdated(false);
    setViewMode("descriptor");
    setFilters({
      statuses: descriptorStatuses,
      search: "",
    });
    setSkipFiltersUpdate(true);
  };

  const handleNavigate = (selectedEnrollment) => {
    if (!selectedEnrollment) return;

    if (isAdmin || userInfo?.is_active) {
      dispatch(UserActions.userSetProp("nextManageEnrollmentStep", 2));
      navigate(`${pathname}/${selectedEnrollment.id}/alerts`);
      return;
    }

    notification.error({
      message: t("Enrollments are not editable"),
      description: t(
        "Enrollments are not editable in inactive business accounts. Please check Billing settings."
      ),
      closeIcon: <Text>{t("Dismiss")}</Text>,
      style: { minWidth: "495px" },
    });
  };

  const handleStatusChange = (descriptor, option) => {
    if (!descriptor?.billing_descriptor_id || !option?.key) return;

    dispatch(
      UserActions.updateAdminDescriptorStatusRequest({
        id: descriptor.billing_descriptor_id,
        status: option.key,
        enrollment_name: enrollment?.name?.toLowerCase(),
      }),
    );
  };

  const handleDescriptorTypeChange = (descriptor, option) => {
    if (!descriptor?.billing_descriptor_id || !option?.key) return;

    dispatch(
      UserActions.updateAdminDescriptorTypeRequest({
        id: descriptor.billing_descriptor_id,
        descriptor_type: option.key,
      }),
    );
  };

  useEffect(() => {
    if (!businessAccountId) return;
    dispatch(
      UserActions.getAdminEnrollmentsRequest({
        business_account_id: businessAccountId,
      })
    );
  }, [businessAccountId, dispatch]);

  if (!enrollment) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 440 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <div className="enrollments-page" data-tour="enrollment">
      <TitleSection
        title={`Enrollment for ${businessAccount?.name}`}
        link={businessAccount?.url}
      />

      <EnrollmentTabs
        variant="admin"
        options={enrollmentOptions}
        value={activeTab}
        onChange={handleTabChange}
        className="enrollment-tabs"
      />

      <Flex gap={24} align="flex-start" className="enrollment-content">
        <EnrollmentCard
          enrollment={enrollment}
          status={enrollment?.connection_status}
        >
          <PermissionButtonExtended
            size="large"
            type="primary"
            icon={btnIcon && <Icon type={btnIcon} style={{ fontSize: 20 }} />}
            className="flex center"
            onClick={() => handleNavigate(enrollment)}
            role={businessAccountRole}
            style={{ width: "100%", height: "36px" }}
          >
            {enrollmentBtnTextMap[enrollment?.name?.toLowerCase()]?.[
              enrollment?.connection_status
            ] || "Add more Descriptors"}
          </PermissionButtonExtended>
        </EnrollmentCard>

        <Flex style={{ flexGrow: 1, overflow: "hidden" }} gap={24} vertical>
          {((!isEmptyDescriptorsList || isFiltersUpdated) || activeTab === "RDR") && (
            <DescriptorListFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showViewToggle={activeTab === "RDR"}
            />
          )}

          {viewMode === "descriptor" ? (
            <AdminDescriptorsList
              enrollment={enrollment}
              descriptors={descriptors}
              loading={loading}
              isFiltered={isFiltersUpdated}
              onLoadMore={fetchMore}
              hasMore={hasMore}
              onStatusChange={handleStatusChange}
              onDescriptorTypeChange={handleDescriptorTypeChange}
            />
          ) : (
            <div className="enrollment-codes-list">
              {codesLoading ? (
                <Flex justify="center" align="center" style={{ minHeight: 160 }}>
                  <Spin size="large" />
                </Flex>
              ) : codes.length ? (
                codes.map((item) => (
                  <EnrollmentCodesListItem key={item.id} item={item} />
                ))
              ) : (
                <Text type="secondary">No codes found.</Text>
              )}
            </div>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

export default AdminEnrollments;
